import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Building2, MapPin, Calendar, FileText, ArrowLeft,
  Landmark, TrendingUp, AlertCircle, Share2, Copy, Image as ImageIcon,
  Phone, Mail, Globe, User as UserIcon, Briefcase, Wallet, Star,
  ShieldAlert, Link as LinkIcon, DollarSign,
} from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';
import { toast } from 'sonner';
import ExplainButton from '../components/ExplainButton';
import ResponsibilityNotice from '../components/ResponsibilityNotice';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatCnpj = (raw) => {
  const d = String(raw || '').replace(/\D/g, '').slice(0, 14);
  if (d.length !== 14) return raw;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
};

const brl = (n) =>
  typeof n === 'number'
    ? n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 })
    : '—';

const Empresa = () => {
  const { cnpj } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const resp = await axios.get(`${API}/public/empresa/${cnpj}`, { timeout: 20000 });
        if (!cancelled) setData(resp.data);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || 'Não foi possível carregar o dossiê.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [cnpj]);

  const share = async () => {
    const url = window.location.href;
    const text = data
      ? `Dossiê público de ${data.nome_fantasia || data.razao_social} (${data.cnpj_formatado}) no Tô de Olho.`
      : 'Dossiê público no Tô de Olho.';
    if (navigator.share) {
      try { await navigator.share({ title: 'Tô de Olho', text, url }); return; } catch (_) { /* fallback to clipboard */ }
    }
    navigator.clipboard?.writeText(`${text}\n${url}`);
    toast.success('Link copiado para a área de transferência.');
  };

  const downloadCard = () => {
    if (!data?.cnpj) return;
    const cardUrl = `${API}/public/empresa/${data.cnpj}/card.png`;
    const a = document.createElement('a');
    a.href = cardUrl;
    a.download = `todeolho-${data.cnpj}.png`;
    a.target = '_blank';
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success('Cartão de compartilhamento aberto.');
  };

  const copyCnpj = () => {
    if (!data) return;
    navigator.clipboard?.writeText(data.cnpj_formatado || data.cnpj);
    toast.success('CNPJ copiado.');
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto py-10 text-center text-sm text-[#475569]" data-testid="empresa-loading">
        Carregando dossiê da empresa...
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto py-10" data-testid="empresa-error">
        <Card className="p-6 border border-[#FCA5A5] bg-[#FEE2E2]">
          <div className="flex items-start gap-2 text-[#991B1B]">
            <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
            <div>
              <p className="font-bold">Não foi possível carregar o dossiê.</p>
              <p className="text-sm mt-1">{error}</p>
              <Button
                variant="outline"
                onClick={() => navigate('/empresa')}
                className="mt-3"
                data-testid="empresa-back-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-1.5" /> Voltar para busca
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  if (!data) return null;

  const resumo = data.resumo || {};
  const emendasResumo = data.emendas_resumo || { total_recebido: 0, num_emendas: 0, anos: [], top_municipios: [] };
  const alertas = data.alertas_integridade || [];
  const alertasCriticos = alertas.filter((a) => a.level === 'danger');
  const alertasAtencao = alertas.filter((a) => a.level === 'warning');
  const nivelRisco = data.nivel_risco || 'ok';
  const nomeExibicao = data.nome_fantasia || data.razao_social;
  const fonte = data.portal_disponivel ? 'Portal da Transparência' : 'Receita Federal';

  const riscoStyles = {
    critico: {
      badge: 'bg-[#7F1D1D] text-white',
      border: 'border-[#B91C1C]',
      bg: 'bg-[#FEE2E2]',
      label: 'RISCO CRÍTICO',
      icon: <ShieldAlert className="w-5 h-5" />,
    },
    atencao: {
      badge: 'bg-[#B45309] text-white',
      border: 'border-[#F59E0B]',
      bg: 'bg-[#FEF3C7]',
      label: 'PONTOS DE ATENÇÃO',
      icon: <AlertCircle className="w-5 h-5" />,
    },
    ok: {
      badge: 'bg-[#166534] text-white',
      border: 'border-[#22C55E]',
      bg: 'bg-[#DCFCE7]',
      label: 'SEM ALERTAS DETECTADOS',
      icon: <Building2 className="w-5 h-5" />,
    },
  };
  const rs = riscoStyles[nivelRisco] || riscoStyles.ok;

  return (
    <div className="max-w-4xl mx-auto space-y-4" data-testid="empresa-dossie-page">
      <button
        type="button"
        onClick={() => navigate('/empresa')}
        className="flex items-center gap-1 text-sm text-[#475569] hover:text-[#006030] transition-colors"
        data-testid="empresa-back-link"
      >
        <ArrowLeft className="w-4 h-4" /> Voltar para busca
      </button>

      {/* Identity card */}
      <Card className="p-5 md:p-6 border border-[#E2E8F0]" data-testid="empresa-identity-card">
        <div className="flex flex-col sm:flex-row items-start gap-4">
          <div className="w-14 h-14 rounded-xl bg-[#E8F5E9] flex items-center justify-center shrink-0">
            <Building2 className="w-7 h-7 text-[#006030]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-bold uppercase tracking-wider text-[#94A3B8]">
              {data.tipo}
            </p>
            <h1 className="text-lg md:text-2xl font-black text-[#0F1C2E] leading-tight break-words">
              {nomeExibicao}
            </h1>
            {data.razao_social && data.razao_social !== nomeExibicao && (
              <p className="text-xs text-[#475569] mt-0.5">{data.razao_social}</p>
            )}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-[#475569]">
              <button
                onClick={copyCnpj}
                className="inline-flex items-center gap-1 hover:text-[#006030]"
                data-testid="empresa-cnpj-copy"
              >
                <Copy className="w-3 h-3" /> {data.cnpj_formatado}
              </button>
              {(data.municipio || data.uf) && (
                <span className="inline-flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> {data.municipio}
                  {data.uf ? ` / ${data.uf}` : ''}
                </span>
              )}
              {data.abertura && (
                <span className="inline-flex items-center gap-1">
                  <Calendar className="w-3 h-3" /> Aberta em {data.abertura}
                </span>
              )}
            </div>
            {data.atividade_principal && (
              <p className="text-xs text-[#475569] mt-1.5">
                Atividade principal. {data.atividade_principal}.
              </p>
            )}
          </div>
          <div className="flex gap-2 sm:self-start flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={downloadCard}
              className="border-[#006030] text-[#006030] hover:bg-[#E8F5E9]"
              data-testid="empresa-card-btn"
            >
              <ImageIcon className="w-3.5 h-3.5 mr-1.5" /> Cartão
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={share}
              className="border-[#006030] text-[#006030] hover:bg-[#E8F5E9]"
              data-testid="empresa-share-btn"
            >
              <Share2 className="w-3.5 h-3.5 mr-1.5" /> Compartilhar
            </Button>
          </div>
        </div>
      </Card>

      {/* Badge de risco investigativo — sempre visível */}
      <div
        className={`flex items-center gap-3 p-3 rounded-lg border-2 ${rs.border} ${rs.bg}`}
        data-testid="empresa-risco-badge"
      >
        <div className={`${rs.badge} px-2.5 py-1 rounded-md flex items-center gap-1.5 font-black text-xs uppercase tracking-wider`}>
          {rs.icon}
          <span data-testid="empresa-risco-label">{rs.label}</span>
        </div>
        <p className="text-xs text-[#0F1C2E] flex-1 leading-snug">
          {nivelRisco === 'critico' && `${alertasCriticos.length} alerta(s) crítico(s) detectado(s).`}
          {nivelRisco === 'atencao' && `${alertasAtencao.length} ponto(s) de atenção identificado(s).`}
          {nivelRisco === 'ok' && 'Nenhum alerta automático detectado nesta análise.'}
        </p>
      </div>

      {/* Alertas de integridade — ordenados por gravidade (danger, warning, info) */}
      {alertas.length > 0 && (
        <div className="space-y-2" data-testid="empresa-alertas">
          {alertas.map((a, i) => {
            const colors = {
              danger:  'bg-[#FEE2E2] border-[#B91C1C] text-[#7F1D1D]',
              warning: 'bg-[#FEF3C7] border-[#F59E0B] text-[#78350F]',
              info:    'bg-[#DBEAFE] border-[#3B82F6] text-[#1E3A8A]',
            };
            const iconBg = {
              danger: 'bg-[#B91C1C] text-white',
              warning: 'bg-[#F59E0B] text-white',
              info: 'bg-[#3B82F6] text-white',
            };
            return (
              <div
                key={i}
                className={`p-3 rounded-md border-l-4 text-sm flex items-start gap-3 ${colors[a.level] || colors.info}`}
                data-testid={`empresa-alerta-${a.code}`}
              >
                <div className={`w-8 h-8 rounded-full ${iconBg[a.level] || iconBg.info} flex items-center justify-center shrink-0 font-black text-sm`}>
                  {a.level === 'danger' ? '!' : a.level === 'warning' ? '△' : 'i'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-black leading-tight">{a.title}</p>
                  <p className="text-xs mt-1 leading-relaxed">{a.message}</p>
                  {a.socios_cruzados && a.socios_cruzados.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {a.socios_cruzados.slice(0, 5).map((sc, idx) => (
                        <RouterLink
                          key={idx}
                          to={`/empresa/${sc.outra_empresa.cnpj}`}
                          className="flex items-center gap-2 text-xs hover:underline"
                          data-testid={`socio-cruzado-${idx}`}
                        >
                          <LinkIcon className="w-3 h-3 shrink-0" />
                          <span className="truncate">
                            <strong>{sc.socio_nome}</strong> · também em <strong>{sc.outra_empresa.nome}</strong>
                          </span>
                        </RouterLink>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Emendas Parlamentares recebidas — dados reais do Tesouro Transparente */}
      {emendasResumo.total_recebido > 0 && (
        <Card className="p-5 border-2 border-[#006030]/30 bg-[#F0FDF4]" data-testid="empresa-emendas-card">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-10 h-10 rounded-full bg-[#006030] flex items-center justify-center shrink-0">
              <DollarSign className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-sm font-black text-[#0F1C2E] uppercase tracking-wider">
                Emendas parlamentares recebidas
              </h2>
              <p className="text-[10px] text-[#94A3B8]">Fonte. Tesouro Transparente (dados reais)</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div className="bg-white p-3 rounded-md border border-[#006030]/20" data-testid="emenda-kpi-total">
              <p className="text-[10px] uppercase tracking-wider text-[#94A3B8] font-bold">Total recebido</p>
              <p className="text-lg md:text-xl font-black text-[#006030] mt-1">{brl(emendasResumo.total_recebido)}</p>
            </div>
            <div className="bg-white p-3 rounded-md border border-[#006030]/20" data-testid="emenda-kpi-num">
              <p className="text-[10px] uppercase tracking-wider text-[#94A3B8] font-bold">Pagamentos</p>
              <p className="text-lg md:text-xl font-black text-[#0F1C2E] mt-1">{emendasResumo.num_emendas}</p>
            </div>
          </div>
          {emendasResumo.anos?.length > 0 && (
            <div className="mb-3">
              <p className="text-[10px] uppercase tracking-wider text-[#94A3B8] font-bold mb-2">Evolução por ano</p>
              <div className="space-y-1.5">
                {emendasResumo.anos.map((a) => {
                  const max = Math.max(...emendasResumo.anos.map((x) => x.valor));
                  const pct = max ? Math.max(4, Math.round((a.valor / max) * 100)) : 0;
                  return (
                    <div key={a.ano} className="flex items-center gap-2" data-testid={`emenda-ano-${a.ano}`}>
                      <span className="w-10 text-xs text-[#475569] font-medium">{a.ano}</span>
                      <div className="flex-1 h-4 bg-white rounded overflow-hidden border border-[#006030]/20">
                        <div className="h-full bg-[#006030]" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-xs text-[#0F1C2E] font-semibold w-20 text-right">{brl(a.valor)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {emendasResumo.top_municipios?.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-[#94A3B8] font-bold mb-2">Onde o dinheiro chegou</p>
              <div className="space-y-1">
                {emendasResumo.top_municipios.map((m, idx) => {
                  const canDrill = !!m.cod_ibge;
                  const row = (
                    <div className="flex items-center justify-between text-xs py-1" data-testid={`emenda-muni-${idx}`}>
                      <span className="text-[#0F1C2E] truncate">
                        {m.nome} <span className="text-[#94A3B8]">/ {m.uf}</span>
                        {m.num_emendas && <span className="text-[#94A3B8] ml-1">· {m.num_emendas} pgto.</span>}
                      </span>
                      <span className="font-bold text-[#006030] shrink-0 ml-2">{brl(m.valor)}</span>
                    </div>
                  );
                  return canDrill ? (
                    <RouterLink
                      key={m.cod_ibge || idx}
                      to={`/emendas/municipio/${m.cod_ibge}`}
                      className="block hover:bg-white/60 -mx-1 px-1 rounded"
                      data-testid={`emenda-muni-link-${idx}`}
                    >
                      {row}
                    </RouterLink>
                  ) : (
                    <div key={idx}>{row}</div>
                  );
                })}
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Portal availability banner */}
      {!data.portal_disponivel && data.message && (
        <Card
          className="p-4 border border-[#FDE68A] bg-[#FEF3C7]"
          data-testid="empresa-portal-warning"
        >
          <p className="text-xs text-[#92400E] leading-relaxed">
            <strong>Dados de contratos federais em preparação.</strong>{' '}
            {data.message}
          </p>
        </Card>
      )}

      {/* SEÇÃO — Identificação detalhada */}
      {(data.porte || data.natureza_juridica || data.capital_social > 0) && (
        <Card className="p-5 border border-[#E2E8F0]" data-testid="empresa-detalhes-card">
          <h2 className="text-sm font-black text-[#0F1C2E] uppercase tracking-wider mb-3">
            Identificação
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            {data.porte && (
              <div className="flex items-start gap-2" data-testid="empresa-porte">
                <Briefcase className="w-4 h-4 text-[#94A3B8] mt-0.5 shrink-0" />
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-[#94A3B8] font-bold">Porte</p>
                  <p className="text-[#0F1C2E]">{data.porte}</p>
                </div>
              </div>
            )}
            {data.natureza_juridica && (
              <div className="flex items-start gap-2" data-testid="empresa-natureza">
                <Landmark className="w-4 h-4 text-[#94A3B8] mt-0.5 shrink-0" />
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-[#94A3B8] font-bold">Natureza jurídica</p>
                  <p className="text-[#0F1C2E]">{data.natureza_juridica}</p>
                </div>
              </div>
            )}
            {data.capital_social > 0 && (
              <div className="flex items-start gap-2" data-testid="empresa-capital">
                <Wallet className="w-4 h-4 text-[#94A3B8] mt-0.5 shrink-0" />
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-[#94A3B8] font-bold">Capital social</p>
                  <p className="text-[#0F1C2E]">{brl(data.capital_social)}</p>
                </div>
              </div>
            )}
            {data.situacao && (
              <div className="flex items-start gap-2" data-testid="empresa-situacao">
                <div className={`w-2 h-2 rounded-full mt-2 shrink-0 ${
                  data.situacao === 'ATIVA' ? 'bg-[#006030]' : 'bg-[#C62828]'
                }`} />
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-[#94A3B8] font-bold">Situação cadastral</p>
                  <p className="text-[#0F1C2E]">{data.situacao}</p>
                </div>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* SEÇÃO — Contato e endereço */}
      {(data.endereco?.completo || data.contato?.telefone_receita || data.contato?.email_receita || data.contato?.site_google) && (
        <Card className="p-5 border border-[#E2E8F0]" data-testid="empresa-contato-card">
          <h2 className="text-sm font-black text-[#0F1C2E] uppercase tracking-wider mb-3">
            Contato e endereço
          </h2>
          <div className="space-y-2.5 text-sm">
            {data.endereco?.completo && (
              <div className="flex items-start gap-2" data-testid="empresa-endereco">
                <MapPin className="w-4 h-4 text-[#94A3B8] mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <p className="text-[10px] uppercase tracking-wider text-[#94A3B8] font-bold">Endereço da Receita</p>
                  <p className="text-[#0F1C2E]">{data.endereco.completo}</p>
                </div>
              </div>
            )}
            {data.contato?.telefone_receita && (
              <div className="flex items-start gap-2" data-testid="empresa-telefone">
                <Phone className="w-4 h-4 text-[#94A3B8] mt-0.5 shrink-0" />
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-[#94A3B8] font-bold">Telefone cadastrado</p>
                  <a
                    href={`tel:${data.contato.telefone_receita.replace(/\D/g, '')}`}
                    className="text-[#006030] font-medium hover:underline"
                  >
                    {data.contato.telefone_receita}
                  </a>
                  {data.contato.telefone_secundario && (
                    <span className="text-[#94A3B8] ml-2 text-xs">
                      / {data.contato.telefone_secundario}
                    </span>
                  )}
                </div>
              </div>
            )}
            {data.contato?.email_receita && (
              <div className="flex items-start gap-2" data-testid="empresa-email">
                <Mail className="w-4 h-4 text-[#94A3B8] mt-0.5 shrink-0" />
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-[#94A3B8] font-bold">E-mail cadastrado</p>
                  <a
                    href={`mailto:${data.contato.email_receita}`}
                    className="text-[#006030] font-medium hover:underline break-all"
                  >
                    {data.contato.email_receita}
                  </a>
                </div>
              </div>
            )}
            {data.contato?.site_google && (
              <div className="flex items-start gap-2" data-testid="empresa-site">
                <Globe className="w-4 h-4 text-[#94A3B8] mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <p className="text-[10px] uppercase tracking-wider text-[#94A3B8] font-bold">Site oficial</p>
                  <a
                    href={data.contato.site_google}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#006030] font-medium hover:underline break-all"
                  >
                    {data.contato.site_google}
                  </a>
                </div>
              </div>
            )}
            {data.contato?.avaliacao_google && (
              <div className="flex items-start gap-2" data-testid="empresa-avaliacao">
                <Star className="w-4 h-4 text-[#F0D000] mt-0.5 shrink-0" />
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-[#94A3B8] font-bold">Avaliação Google</p>
                  <p className="text-[#0F1C2E]">{data.contato.avaliacao_google} de 5</p>
                </div>
              </div>
            )}
          </div>
          {!data.google_disponivel && (
            <p className="mt-3 text-[10px] text-[#94A3B8] italic">
              Site, telefone público e avaliação disponíveis quando a chave do Google Places estiver configurada.
            </p>
          )}
        </Card>
      )}

      {/* SEÇÃO — Sócios e administradores */}
      {data.socios?.length > 0 && (
        <Card className="p-5 border border-[#E2E8F0]" data-testid="empresa-socios-card">
          <h2 className="text-sm font-black text-[#0F1C2E] uppercase tracking-wider mb-3">
            Sócios e administradores
          </h2>
          <div className="space-y-2">
            {data.socios.map((s, idx) => (
              <div
                key={idx}
                className="flex items-start gap-3 py-2 border-b border-[#F1F5F9] last:border-0"
                data-testid={`socio-${idx}`}
              >
                <div className="w-8 h-8 rounded-full bg-[#F4F6F8] flex items-center justify-center shrink-0">
                  <UserIcon className="w-4 h-4 text-[#475569]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#0F1C2E] leading-tight">{s.nome}</p>
                  <p className="text-xs text-[#475569] mt-0.5">
                    {s.qualificacao}
                    {s.data_entrada && ` · desde ${s.data_entrada}`}
                    {s.faixa_etaria && ` · ${s.faixa_etaria.toLowerCase()}`}
                  </p>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-3 text-[10px] text-[#94A3B8] italic">
            Dados do Quadro de Sócios e Administradores (QSA) da Receita Federal.
          </p>
        </Card>
      )}

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-4 border border-[#E2E8F0]" data-testid="kpi-total-recebido">
          <div className="flex items-center gap-1.5 text-[#475569] text-[10px] font-bold uppercase tracking-wider">
            <TrendingUp className="w-3 h-3" /> Recebido
          </div>
          <p className="text-lg md:text-xl font-black text-[#006030] mt-1">
            {brl(resumo.total_recebido)}
          </p>
          <div className="mt-1.5">
            <ExplainButton
              contexto="total recebido pela empresa em contratos com o governo federal"
              valor={brl(resumo.total_recebido)}
              tipo="valor"
              fonte={fonte}
              entidade={nomeExibicao}
              testId="explain-total-recebido"
            />
          </div>
        </Card>

        <Card className="p-4 border border-[#E2E8F0]" data-testid="kpi-num-contratos">
          <div className="flex items-center gap-1.5 text-[#475569] text-[10px] font-bold uppercase tracking-wider">
            <FileText className="w-3 h-3" /> Contratos
          </div>
          <p className="text-lg md:text-xl font-black text-[#0F1C2E] mt-1">
            {resumo.num_contratos || 0}
          </p>
          {resumo.num_contratos > 0 && (
            <div className="mt-1.5">
              <ExplainButton
                contexto="número de contratos federais assinados pela empresa"
                valor={String(resumo.num_contratos)}
                tipo="contrato"
                fonte={fonte}
                entidade={nomeExibicao}
                testId="explain-num-contratos"
              />
            </div>
          )}
        </Card>

        <Card className="p-4 border border-[#E2E8F0]" data-testid="kpi-num-orgaos">
          <div className="flex items-center gap-1.5 text-[#475569] text-[10px] font-bold uppercase tracking-wider">
            <Landmark className="w-3 h-3" /> Órgãos
          </div>
          <p className="text-lg md:text-xl font-black text-[#0F1C2E] mt-1">
            {resumo.num_orgaos || 0}
          </p>
        </Card>

        <Card className="p-4 border border-[#E2E8F0]" data-testid="kpi-num-convenios">
          <div className="flex items-center gap-1.5 text-[#475569] text-[10px] font-bold uppercase tracking-wider">
            Convênios
          </div>
          <p className="text-lg md:text-xl font-black text-[#0F1C2E] mt-1">
            {resumo.num_convenios || 0}
          </p>
        </Card>
      </div>

      {/* Órgãos pagadores */}
      {data.orgaos_pagadores?.length > 0 && (
        <Card className="p-5 border border-[#E2E8F0]" data-testid="empresa-orgaos-card">
          <h2 className="text-sm font-black text-[#0F1C2E] uppercase tracking-wider mb-3">
            Quem pagou
          </h2>
          <div className="space-y-2">
            {data.orgaos_pagadores.map((o, idx) => (
              <div
                key={idx}
                className="flex items-center justify-between gap-3 py-2 border-b border-[#F1F5F9] last:border-0"
                data-testid={`orgao-${idx}`}
              >
                <p className="text-sm text-[#0F1C2E] flex-1 min-w-0 truncate">{o.nome}</p>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-sm font-bold text-[#006030]">{brl(o.valor)}</span>
                  <ExplainButton
                    contexto={`valor pago pelo órgão ${o.nome} para ${nomeExibicao}`}
                    valor={brl(o.valor)}
                    tipo="valor"
                    fonte={fonte}
                    entidade={nomeExibicao}
                    testId={`explain-orgao-${idx}`}
                  />
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Linha do tempo por ano */}
      {data.por_ano?.length > 0 && (
        <Card className="p-5 border border-[#E2E8F0]" data-testid="empresa-timeline-card">
          <h2 className="text-sm font-black text-[#0F1C2E] uppercase tracking-wider mb-3">
            Evolução por ano
          </h2>
          <div className="space-y-2">
            {data.por_ano.map((p) => {
              const maxVal = Math.max(...data.por_ano.map((x) => x.valor));
              const pct = maxVal ? Math.max(4, Math.round((p.valor / maxVal) * 100)) : 0;
              return (
                <div key={p.ano} className="flex items-center gap-3" data-testid={`ano-${p.ano}`}>
                  <span className="w-12 text-xs text-[#475569] font-medium">{p.ano}</span>
                  <div className="flex-1 h-5 bg-[#F1F5F9] rounded overflow-hidden">
                    <div
                      className="h-full bg-[#006030] rounded transition-all"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span className="text-xs text-[#0F1C2E] font-semibold w-24 text-right">{brl(p.valor)}</span>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Amostra de contratos */}
      {data.contratos_amostra?.length > 0 && (
        <Card className="p-5 border border-[#E2E8F0]" data-testid="empresa-contratos-card">
          <h2 className="text-sm font-black text-[#0F1C2E] uppercase tracking-wider mb-3">
            Últimos contratos
          </h2>
          <div className="space-y-3">
            {data.contratos_amostra.map((c, idx) => (
              <div
                key={idx}
                className="p-3 rounded-md border border-[#F1F5F9] hover:border-[#006030]/40 transition-colors"
                data-testid={`contrato-${idx}`}
              >
                <p className="text-sm font-semibold text-[#0F1C2E] leading-snug">
                  {c.objeto}
                </p>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs text-[#475569]">
                  <span className="font-bold text-[#006030]">{brl(c.valor)}</span>
                  {c.data_assinatura && (
                    <span className="inline-flex items-center gap-1">
                      <Calendar className="w-3 h-3" /> {c.data_assinatura}
                    </span>
                  )}
                  {c.orgao && <span className="truncate">{c.orgao}</span>}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Fonte */}
      <p className="text-[11px] text-[#94A3B8] text-center py-4">
        Dados oficiais. Fonte. {fonte}. Cache renovado a cada 24 horas.
      </p>

      <ResponsibilityNotice
        source={fonte}
        sourceUrl={data.portal_disponivel ? 'https://portaldatransparencia.gov.br' : 'https://brasilapi.com.br'}
      />
    </div>
  );
};

export default Empresa;
